import {api} from "@/shared/api";

import {PresenceBoardHelper} from "@/features/presenceboard/presenceBoardHelper";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import {debounce} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {AbsentUser, Group, HolidayUser, InactiveUser, NowActiveUser, NoWorkDayUser, PresenceBoardResponse} from "@/shared/timr";
import {PresenceBoardCategory, PresenceBoardFilterOptions, PresenceBoardVisibility} from "@/shared/timrTypes";

export interface PresenceBoardConfig {
	showProjectTimeInPresenceBoard: boolean;
	presenceBoardVisibility: PresenceBoardVisibility;
}

export class PresenceBoardPage {
	private groupDropdown?: GroupDropdown;
	private config: PresenceBoardConfig;
	private presenceBoardFilterOption = BrowserStorageSettings.getPresenceBoardTypeFilter() ?? PresenceBoardFilterOptions.ALL;
	private showPresenceBoardOfOtherUsers: boolean;
	private activeView: JQuery;
	private absentView: JQuery;
	private presenceBoardGroupButton: JQuery;
	private presenceBoardAllButton: JQuery;
	private presenceBoardTypeAllButton: JQuery;
	private presenceBoardTypeActiveButton: JQuery;
	private presenceBoardTypeBreakButton: JQuery;
	private presenceBoardTypeInactiveButton: JQuery;
	private getPresenceBoardUsersRequest: JQuery.jqXHR;
	private absentUsers = 0;
	private nameFilter = "";

	private presenceBoardHelper: PresenceBoardHelper;

	constructor(config: PresenceBoardConfig) {
		this.config = config;
		this.initPresenceBoardShowOtherUsersConfig();
		this.initFilter();
		this.init();

		this.presenceBoardHelper = new PresenceBoardHelper({
			displayRunningProjectTime: this.config.showProjectTimeInPresenceBoard
		});
	}

	private initPresenceBoardShowOtherUsersConfig() {
		// showPresenceBoardOfOtherUsers (true == all, false == group)
		this.showPresenceBoardOfOtherUsers = PresenceBoardHelper.setPresenceBoardSessionSettings(this.config.presenceBoardVisibility);
		$("#presence-board-view-button-group").toggle(this.config.presenceBoardVisibility === PresenceBoardVisibility.ALL);
	}

	private init() {
		this.presenceBoardTypeAllButton = $("#presenceBoardTypeAllButton");
		this.presenceBoardTypeActiveButton = $("#presenceBoardTypeActiveButton");
		this.presenceBoardTypeBreakButton = $("#presenceBoardTypeBreakButton");
		this.presenceBoardTypeInactiveButton = $("#presenceBoardTypeInactiveButton");
		this.presenceBoardAllButton = $("#presenceBoardAllButton");
		this.presenceBoardGroupButton = $("#presenceBoardGroupButton");

		this.presenceBoardTypeAllButton.click(() => this.togglePresenceBoardFilterButtons(PresenceBoardFilterOptions.ALL));
		this.presenceBoardTypeActiveButton.click(() => this.togglePresenceBoardFilterButtons(PresenceBoardFilterOptions.ACTIVE));
		this.presenceBoardTypeBreakButton.click(() => this.togglePresenceBoardFilterButtons(PresenceBoardFilterOptions.BREAK));
		this.presenceBoardTypeInactiveButton.click(() => this.togglePresenceBoardFilterButtons(PresenceBoardFilterOptions.INACTIVE));

		this.presenceBoardGroupButton.click(() => this.toggleShowPresenceBoardOfOtherUsersButtons(false));
		this.presenceBoardAllButton.click(() => this.toggleShowPresenceBoardOfOtherUsersButtons(true));

		this.activeView = $("#presenceBoardActiveView");
		this.absentView = $("#presenceBoardAbsentView");

		$("#usersFilterName").val(BrowserStorageSettings.getPresenceBoardNameFilter() ?? "");

		this.updatePresenceBoardButtons();
	}

	private initFilter() {
		if (SecurityStatus.isCorporateFeaturesAllowed()) {
			this.groupDropdown = new GroupDropdown({
				rootElement: $("#presenceBoardFilterGroupQueryDropdown"),
				allowSelectionOfNotAssigned: true,
				emptyMessage: messages["group.filter.all"],
				clearEntryMessage: messages["group.filter.all"],
			}, {
				loadRootNodesURL: "reports/presenceBoard/loadRootGroups.json",
				loadSubNodesURL: "reports/presenceBoard/loadSubGroups.json",
				filterNodesURL: "reports/presenceBoard/filterGroups.json"
			});
			const defaultGroup = BrowserStorageSettings.getPresenceBoardGroupFilter();
			if (defaultGroup) {
				this.groupDropdown?.setSelection(defaultGroup.id, defaultGroup.name, defaultGroup.breadCrumbs, defaultGroup);
			}
			$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group: Group) => {
				BrowserStorageSettings.setPresenceBoardGroupFilter(group);
				this.updateCards();
			});
		}
		const filterBoard = () => {
			this.nameFilter = $("#usersFilterName").val() as string;
			BrowserStorageSettings.setPresenceBoardNameFilter(this.nameFilter);
			this.updateCards();
		};
		$("#usersFilterName").on("input", debounce(filterBoard, this, 350));
	}

	private updateCards() {
		this.showLoadingAnimation();

		if (this.config.presenceBoardVisibility === PresenceBoardVisibility.OFF) {
			return;
		}

		this.getPresenceBoardUsersRequest?.abort();
		const params = [];
		params.push({
			name: "groupId",
			value: SecurityStatus.isCorporateFeaturesAllowed() ? this.groupDropdown?.getSelectedId() : null
		});
		params.push({name: "nameFilter", value: this.nameFilter});
		params.push({
			name: "presenceBoardCategoryFilter",
			value: this.presenceBoardFilterOption || PresenceBoardFilterOptions.ALL
		});
		params.push({name: "showPresenceBoardOfOtherUsers", value: this.showPresenceBoardOfOtherUsers});
		this.getPresenceBoardUsersRequest = api("reports/getPresenceBoardUsers.json")
			.post(params)
			.done((response: PresenceBoardResponse) => {
				SecurityStatus.update(response.securityStatus);

				this.activeView.empty();
				this.absentView.empty();
				this.absentUsers = 0;
				this.config.presenceBoardVisibility = response.presenceBoardVisibility;
				this.reloadSessionSettings();
				$("#presenceBoardNoUser").toggle(response.data.length === 0);
				for (const presenceBoardUser of response.data) {
					switch (presenceBoardUser.presenceBoardUserType) {
						case PresenceBoardCategory.NOW_ACTIVE:
							this.processNowActiveUser(presenceBoardUser as NowActiveUser);
							break;
						case PresenceBoardCategory.INACTIVE:
							this.processInactiveUser(presenceBoardUser as InactiveUser);
							break;
						case PresenceBoardCategory.HOLIDAY:
							this.processHolidayUser(presenceBoardUser as HolidayUser);
							break;
						case PresenceBoardCategory.NO_WORK_DAY:
							this.processNoWorkingDayUser(presenceBoardUser as NoWorkDayUser);
							break;
						case PresenceBoardCategory.ABSENT:
							this.processAbsentUser(presenceBoardUser as AbsentUser);
							break;
					}
				}
			})
			.always(
				() => {
					this.toggleAbsenceView();
				}
			);
	}

	private toggleAbsenceView() {
		const container = $("#presenceBoardAbsentTodayContainer").hide();
		const info = $("#presenceBoardNoPlannedAbsences").hide();
		if (this.absentUsers === 0 && this.presenceBoardFilterOption === PresenceBoardFilterOptions.ALL && this.nameFilter.length <= 0) {
			info.show();
			container.show();
		} else if (this.absentUsers !== 0 &&
			(this.presenceBoardFilterOption === PresenceBoardFilterOptions.INACTIVE ||
				this.presenceBoardFilterOption === PresenceBoardFilterOptions.ALL)) {
			container.show();
		}
	}

	private togglePresenceBoardFilterButtons(filter: PresenceBoardFilterOptions) {
		if (this.presenceBoardFilterOption !== filter) {
			this.presenceBoardFilterOption = filter;
			BrowserStorageSettings.setPresenceBoardTypeFilter(filter);
			this.updatePresenceBoardButtons();
		}
	}

	private updatePresenceBoardButtons(updateCards: boolean = true) {
		this.presenceBoardTypeAllButton.toggleClass("btn-primary", this.presenceBoardFilterOption === PresenceBoardFilterOptions.ALL);
		this.presenceBoardTypeActiveButton.toggleClass("btn-primary", this.presenceBoardFilterOption === PresenceBoardFilterOptions.ACTIVE);
		this.presenceBoardTypeBreakButton.toggleClass("btn-primary", this.presenceBoardFilterOption === PresenceBoardFilterOptions.BREAK);
		this.presenceBoardTypeInactiveButton.toggleClass("btn-primary", this.presenceBoardFilterOption === PresenceBoardFilterOptions.INACTIVE);

		this.presenceBoardGroupButton.toggleClass("btn-primary", !this.showPresenceBoardOfOtherUsers);
		this.presenceBoardAllButton.toggleClass("btn-primary", this.showPresenceBoardOfOtherUsers);

		$("#group-dropdown").toggle(this.showPresenceBoardOfOtherUsers);

		if (updateCards) {
			this.updateCards();
		}
	}

	private toggleShowPresenceBoardOfOtherUsersButtons(show: boolean) {
		if (this.showPresenceBoardOfOtherUsers !== show) {
			this.showPresenceBoardOfOtherUsers = show;
			BrowserStorageSettings.setPresenceBoardShowAllFilter(show);
			this.updatePresenceBoardButtons();
		}
	}

	private processNowActiveUser(presenceBoardUser: NowActiveUser) {
		this.activeView.append(this.presenceBoardHelper.getActiveUserCard(presenceBoardUser));
	}

	private processInactiveUser(presenceBoardUser: InactiveUser) {
		this.activeView.append(this.presenceBoardHelper.getInactiveUserCard(presenceBoardUser));
	}

	private processAbsentUser(presenceBoardUser: AbsentUser) {
		this.absentUsers++;
		this.absentView.append(this.presenceBoardHelper.getAbsentUserCard(presenceBoardUser));
	}

	private processHolidayUser(presenceBoardUser: HolidayUser) {
		this.absentUsers++;
		this.absentView.append(this.presenceBoardHelper.getHolidayUserCard(presenceBoardUser));
	}

	private processNoWorkingDayUser(presenceBoardUser: NoWorkDayUser) {
		this.absentUsers++;
		this.absentView.append(this.presenceBoardHelper.getNoWorkingDayUserCard(presenceBoardUser));
	}

	private showLoadingAnimation() {
		if (this.activeView.children().length === 0) {
			// Add dummy card for loading
			$("#presenceBoardNoUser").hide();
			this.activeView.append(
				this.presenceBoardHelper.getPresenceBoardUserCard({
					fullName: "",
					categoryDisplay: "",
					colorType: "inactive"
				})
			);
		}
		// add .loading class to all cards inside a presence board view
		$(".presence-board-view").children(".presence-board-card")
			.addClass("loading")
			.children(".panel-presence-board")    // select the panel specifically
			.attr("data-presence-state", "inactive")
			.css("height", this.config.showProjectTimeInPresenceBoard ? "124px" : "68px")
			.children().empty();	// remove content
	}

	private reloadSessionSettings() {
		this.initPresenceBoardShowOtherUsersConfig();
		this.updatePresenceBoardButtons(false);
	}
}
